import { ReactNode, useState, useEffect, useCallback, useRef } from 'react';
import { SkillsContext, type SkillsSettings, type Plugin, type Skill } from '@/contexts/SkillsContext';
import { useFS } from '@/hooks/useFS';
import { useAppContext } from '@/hooks/useAppContext';
import { createPluginFromUrl, fetchPluginSkills, DEFAULT_PLUGIN_URLS, BUILTIN_SKILLS, generatePluginId } from '@/lib/skills';

interface SkillsProviderProps {
  children: ReactNode;
}

const CONFIG_DIR = '/config';
const SKILLS_CONFIG_PATH = `${CONFIG_DIR}/skills.json`;

const DEFAULT_SETTINGS: SkillsSettings = {
  plugins: [],
};

export function SkillsProvider({ children }: SkillsProviderProps) {
  const { fs } = useFS();
  const { config } = useAppContext();
  const [settings, setSettings] = useState<SkillsSettings>(DEFAULT_SETTINGS);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const defaultPluginsLoadedRef = useRef(false);

  // Initialize settings from VFS on mount
  useEffect(() => {
    const initializeSettings = async () => {
      try {
        const content = await fs.readFile(SKILLS_CONFIG_PATH, 'utf8');
        const data = JSON.parse(content);

        // Migration: Remove old stripe-skill remote plugin if it exists
        // (it's now a built-in plugin)
        const filteredPlugins = data.plugins.filter((p: Plugin) => {
          // Remove the old remote stripe-skill plugin
          if (p.url && p.url.includes('stripe-skill') && !p.isBuiltIn) {
            return false;
          }
          return true;
        });

        setSettings({ ...data, plugins: filteredPlugins });
      } catch {
        setSettings(DEFAULT_SETTINGS);
      } finally {
        setIsInitialized(true);
      }
    };

    initializeSettings();
  }, [fs]);

  // Load default plugins and built-in skills if they haven't been loaded yet
  useEffect(() => {
    if (!isInitialized || defaultPluginsLoadedRef.current) return;

    const loadDefaultPlugins = async () => {
      let hasChanges = false;

      // Add missing built-in skills
      const missingBuiltins = BUILTIN_SKILLS.filter(builtin => {
        return !settings.plugins.some(p => p.id === builtin.id);
      });

      if (missingBuiltins.length > 0) {
        setSettings(prev => ({
          ...prev,
          plugins: [...prev.plugins, ...missingBuiltins],
        }));
        hasChanges = true;
      }

      // Check which default plugins are missing
      const missingDefaultUrls = DEFAULT_PLUGIN_URLS.filter(url => {
        const pluginId = generatePluginId(url);
        return !settings.plugins.some(p => p.id === pluginId);
      });

      if (missingDefaultUrls.length === 0 && !hasChanges) {
        defaultPluginsLoadedRef.current = true;
        return;
      }

      // Load missing default plugins in background (don't block UI)
      for (const url of missingDefaultUrls) {
        try {
          const plugin = await createPluginFromUrl(url, config.corsProxy);
          plugin.isDefault = true;

          setSettings(prev => {
            // Double-check it hasn't been added yet
            if (prev.plugins.some(p => p.id === plugin.id)) {
              return prev;
            }
            return {
              ...prev,
              plugins: [...prev.plugins, plugin],
            };
          });
        } catch (error) {
          console.error(`Failed to load default plugin ${url}:`, error);
        }
      }

      defaultPluginsLoadedRef.current = true;
    };

    loadDefaultPlugins();
  }, [isInitialized, settings.plugins, config.corsProxy]);

  // Save settings to VFS whenever they change
  useEffect(() => {
    if (!isInitialized) return;

    const saveSettings = async () => {
      try {
        // Ensure config directory exists
        try {
          await fs.stat(CONFIG_DIR);
        } catch {
          await fs.mkdir(CONFIG_DIR, { recursive: true });
        }
        await fs.writeFile(SKILLS_CONFIG_PATH, JSON.stringify(settings, null, 2), 'utf8');
      } catch (error) {
        console.error('Failed to save skills settings:', error);
      }
    };

    saveSettings();
  }, [fs, settings, isInitialized]);

  const addPlugin = useCallback(async (url: string) => {
    setIsLoading(true);
    try {
      const plugin = await createPluginFromUrl(url, config.corsProxy);

      // Check if plugin already exists
      if (settings.plugins.some(p => p.id === plugin.id)) {
        throw new Error('Plugin already exists');
      }

      // Check if this is a default plugin URL
      const isDefault = DEFAULT_PLUGIN_URLS.some(defaultUrl =>
        generatePluginId(defaultUrl) === plugin.id
      );
      if (isDefault) {
        plugin.isDefault = true;
      }

      setSettings(prev => ({
        ...prev,
        plugins: [...prev.plugins, plugin],
      }));
    } finally {
      setIsLoading(false);
    }
  }, [config.corsProxy, settings.plugins]);

  const removePlugin = useCallback((id: string) => {
    setSettings(prev => {
      // Find the plugin
      const plugin = prev.plugins.find(p => p.id === id);

      // Don't allow removing default plugins
      if (plugin?.isDefault) {
        console.warn('Cannot remove default plugin:', plugin.name);
        return prev;
      }

      return {
        ...prev,
        plugins: prev.plugins.filter(p => p.id !== id),
      };
    });
  }, []);

  const togglePlugin = useCallback((id: string) => {
    setSettings(prev => ({
      ...prev,
      plugins: prev.plugins.map(p =>
        p.id === id ? { ...p, enabled: !p.enabled } : p
      ),
    }));
  }, []);

  const refreshPlugin = useCallback(async (id: string) => {
    const plugin = settings.plugins.find(p => p.id === id);
    if (!plugin) return;

    // Built-in plugins cannot be refreshed
    if (plugin.isBuiltIn) {
      console.warn('Cannot refresh built-in plugin:', plugin.name);
      return;
    }

    setIsLoading(true);
    try {
      const { skills } = await fetchPluginSkills(plugin.url, config.corsProxy);

      setSettings(prev => ({
        ...prev,
        plugins: prev.plugins.map(p =>
          p.id === id ? { ...p, skills, lastFetched: Date.now() } : p
        ),
      }));
    } finally {
      setIsLoading(false);
    }
  }, [settings.plugins, config.corsProxy]);

  const getEnabledSkills = useCallback((): Skill[] => {
    return settings.plugins
      .filter(p => p.enabled)
      .flatMap(p => p.skills);
  }, [settings.plugins]);

  const getSkillByName = useCallback((name: string): Skill | undefined => {
    for (const plugin of settings.plugins) {
      if (!plugin.enabled) continue;
      const skill = plugin.skills.find(s => s.name === name);
      if (skill) return skill;
    }
    return undefined;
  }, [settings.plugins]);

  return (
    <SkillsContext.Provider
      value={{
        settings,
        addPlugin,
        removePlugin,
        togglePlugin,
        refreshPlugin,
        getEnabledSkills,
        getSkillByName,
        isLoading,
      }}
    >
      {children}
    </SkillsContext.Provider>
  );
}
